import { Tag } from '@carbon/react';
import { ListTable, StatTile, StatTiles, useFormat } from '@pos/admin-ui';
import type { HoComponents } from '@pos/api-client';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { useQuery } from '@tanstack/react-query';
import { hoApi } from '../api';
import { ReportFrame } from './reportFrame';
import { rangeQuery, unwrap, useReportRange } from './reportRange';

type Tables = HoComponents['schemas']['StoreTablesDto'];
type Online = HoComponents['schemas']['OnlineReportDto'];
type OnlineStore = HoComponents['schemas']['StoreOnlineDto'];
type Bookings = HoComponents['schemas']['StoreReservationsDto'];

const n = (value: number | string | null | undefined) => Number(value ?? 0);
const share = (part: number, whole: number) => (whole === 0 ? '—' : `${((part / whole) * 100).toFixed(1)}%`);

/**
 * Operations (REPORTS.md E1, E2, E4): table turnover and RevPASH per store, website and app orders with how fast stores answered
 * them, and table bookings by what became of them.
 */
export function OperationsReportPage() {
  const { t } = useTranslation();
  const format = useFormat();
  const [range, setRange] = useReportRange();
  const r = (key: string, options?: Record<string, unknown>) => t(`reports.operations.${key}` as I18nKey, options);
  const query = { params: { query: rangeQuery(range) } };
  const tables = useQuery({
    queryKey: ['reports', 'tables', range],
    queryFn: () => unwrap<Tables[]>(hoApi.GET('/api/v1/reporting/operations/tables', query)),
  });
  const online = useQuery({
    queryKey: ['reports', 'online', range],
    queryFn: () => unwrap<Online>(hoApi.GET('/api/v1/reporting/operations/online', query)),
  });
  const bookings = useQuery({
    queryKey: ['reports', 'reservations', range],
    queryFn: () => unwrap<Bookings[]>(hoApi.GET('/api/v1/reporting/operations/reservations', query)),
  });
  const tableRows = tables.data ?? [];
  const onlineRows = online.data?.stores ?? [];
  const bookingRows = bookings.data ?? [];
  const sum = <T,>(rows: T[], pick: (row: T) => number | string | null | undefined) =>
    rows.reduce((s, row) => s + n(pick(row)), 0);
  const minutes = (value: number | string | null | undefined) =>
    value == null ? '' : format.number(Math.round(n(value)));
  const seconds = (value: number | string | null | undefined) =>
    value == null ? '' : r('seconds', { count: Math.round(n(value)) });

  return (
    <ReportFrame
      title={r('title')}
      description={r('description')}
      range={range}
      onRange={setRange}
      error={tables.error ?? online.error ?? bookings.error}
      csvName="operations"
      csv={
        tableRows.length + onlineRows.length + bookingRows.length > 0
          ? () => [
              [
                r('store'),
                r('parties'),
                r('covers'),
                r('minutes'),
                r('spendPerGuest'),
                r('tables'),
                r('seats'),
                r('turns'),
                r('revpash'),
              ],
              ...tableRows.map((x) => [
                x.storeName,
                n(x.parties),
                n(x.covers),
                x.averageMinutes == null ? '' : n(x.averageMinutes),
                n(x.spendPerGuest),
                n(x.tables),
                n(x.seats),
                x.turnsPerTableDay == null ? '' : n(x.turnsPerTableDay),
                x.revPash == null ? '' : n(x.revPash),
              ]),
              [],
              [
                r('store'),
                r('onlineOrders'),
                r('accepted'),
                r('rejected'),
                r('cancelled'),
                r('acceptTime'),
                r('acceptP90'),
                r('readyTime'),
                r('deliveryTime'),
              ],
              ...onlineRows.map((x) => [
                x.storeName,
                n(x.orders),
                n(x.accepted),
                n(x.rejected),
                n(x.cancelled),
                x.acceptSeconds == null ? '' : n(x.acceptSeconds),
                x.acceptSecondsP90 == null ? '' : n(x.acceptSecondsP90),
                x.readyMinutes == null ? '' : n(x.readyMinutes),
                x.deliveryMinutes == null ? '' : n(x.deliveryMinutes),
              ]),
              [],
              [
                r('store'),
                r('bookings'),
                r('online'),
                r('seated'),
                r('noShows'),
                r('cancelled'),
                r('declined'),
                r('expired'),
                r('covers'),
              ],
              ...bookingRows.map((x) => [
                x.storeName,
                n(x.bookings),
                n(x.online),
                n(x.seated),
                n(x.noShows),
                n(x.cancelled),
                n(x.declined),
                n(x.expired),
                n(x.covers),
              ]),
            ]
          : undefined
      }
    >
      <StatTiles>
        <StatTile
          label={r('covers')}
          value={format.number(sum(tableRows, (x) => x.covers))}
          caption={r('partiesCaption', { count: sum(tableRows, (x) => x.parties) })}
          isLoading={tables.isLoading}
        />
        <StatTile
          label={r('spendPerGuest')}
          value={format.number(
            sum(tableRows, (x) => x.covers) === 0
              ? 0
              : Math.round(sum(tableRows, (x) => x.sales) / sum(tableRows, (x) => x.covers)),
          )}
          isLoading={tables.isLoading}
        />
        <StatTile
          label={r('onlineOrders')}
          value={format.number(sum(onlineRows, (x) => x.orders))}
          caption={r('rejectedCaption', {
            share: share(
              sum(onlineRows, (x) => x.rejected),
              sum(onlineRows, (x) => x.orders),
            ),
          })}
          isLoading={online.isLoading}
        />
        <StatTile
          label={r('bookings')}
          value={format.number(sum(bookingRows, (x) => x.bookings))}
          caption={r('noShowCaption', {
            share: share(
              sum(bookingRows, (x) => x.noShows),
              sum(bookingRows, (x) => x.bookings),
            ),
          })}
          isLoading={bookings.isLoading}
        />
      </StatTiles>

      <ListTable<Tables & { id: string }>
        title={r('tablesTitle')}
        description={r('tablesNote')}
        columns={[
          { key: 'storeName', header: r('store') },
          { key: 'parties', header: r('parties'), numeric: true, render: (x) => format.number(n(x.parties)) },
          { key: 'covers', header: r('covers'), numeric: true, render: (x) => format.number(n(x.covers)) },
          { key: 'averageMinutes', header: r('minutes'), numeric: true, render: (x) => minutes(x.averageMinutes) },
          {
            key: 'spendPerGuest',
            header: r('spendPerGuest'),
            numeric: true,
            render: (x) => format.number(n(x.spendPerGuest)),
          },
          { key: 'tables', header: r('tables'), numeric: true, render: (x) => format.number(n(x.tables)) },
          { key: 'seats', header: r('seats'), numeric: true, render: (x) => format.number(n(x.seats)) },
          {
            key: 'turnsPerTableDay',
            header: r('turns'),
            numeric: true,
            render: (x) => (x.turnsPerTableDay == null ? '' : format.number(n(x.turnsPerTableDay))),
          },
          {
            key: 'revPash',
            header: r('revpash'),
            numeric: true,
            render: (x) => (x.revPash == null ? '' : format.number(n(x.revPash))),
          },
        ]}
        rows={tableRows.map((x) => ({ ...x, id: x.storeId }))}
        ariaLabel={r('tablesTitle')}
        loading={tables.isLoading}
        empty={{ heading: r('noTables') }}
      />

      <ListTable<OnlineStore & { id: string }>
        title={r('onlineTitle')}
        columns={[
          { key: 'storeName', header: r('store') },
          { key: 'orders', header: r('onlineOrders'), numeric: true, render: (x) => format.number(n(x.orders)) },
          { key: 'accepted', header: r('accepted'), numeric: true, render: (x) => format.number(n(x.accepted)) },
          {
            key: 'rejected',
            header: r('rejected'),
            render: (x) =>
              n(x.rejected) + n(x.cancelled) === 0 ? (
                ''
              ) : (
                <>
                  {n(x.rejected) > 0 && <Tag type="red">{r('rejectedTag', { count: n(x.rejected) })}</Tag>}
                  {n(x.cancelled) > 0 && <Tag type="magenta">{r('cancelledTag', { count: n(x.cancelled) })}</Tag>}
                </>
              ),
          },
          { key: 'acceptSeconds', header: r('acceptTime'), numeric: true, render: (x) => seconds(x.acceptSeconds) },
          {
            key: 'acceptSecondsP90',
            header: r('acceptP90'),
            numeric: true,
            render: (x) => seconds(x.acceptSecondsP90),
          },
          { key: 'readyMinutes', header: r('readyTime'), numeric: true, render: (x) => minutes(x.readyMinutes) },
          {
            key: 'deliveryMinutes',
            header: r('deliveryTime'),
            numeric: true,
            render: (x) => minutes(x.deliveryMinutes),
          },
          { key: 'total', header: t('reports.total'), numeric: true, render: (x) => format.number(n(x.total)) },
        ]}
        rows={onlineRows.map((x) => ({ ...x, id: x.storeId }))}
        ariaLabel={r('onlineTitle')}
        loading={online.isLoading}
        empty={{ heading: r('noOnline') }}
      />
      {(online.data?.reasons.length ?? 0) > 0 && (
        <ListTable<{ id: string; reason: string; count: number | string }>
          title={r('reasons')}
          columns={[
            { key: 'reason', header: r('reason'), render: (x) => x.reason || r('noReason') },
            { key: 'count', header: r('count'), numeric: true, render: (x) => format.number(n(x.count)) },
          ]}
          rows={(online.data?.reasons ?? []).map((x) => ({ ...x, id: x.reason }))}
          ariaLabel={r('reasons')}
        />
      )}

      <ListTable<Bookings & { id: string }>
        title={r('bookingsTitle')}
        columns={[
          { key: 'storeName', header: r('store') },
          { key: 'bookings', header: r('bookings'), numeric: true, render: (x) => format.number(n(x.bookings)) },
          { key: 'online', header: r('online'), numeric: true, render: (x) => format.number(n(x.online)) },
          { key: 'seated', header: r('seated'), numeric: true, render: (x) => format.number(n(x.seated)) },
          {
            key: 'noShows',
            header: r('noShows'),
            numeric: true,
            render: (x) => `${format.number(n(x.noShows))} · ${share(n(x.noShows), n(x.bookings))}`,
          },
          { key: 'cancelled', header: r('cancelled'), numeric: true, render: (x) => format.number(n(x.cancelled)) },
          { key: 'declined', header: r('declined'), numeric: true, render: (x) => format.number(n(x.declined)) },
          { key: 'expired', header: r('expired'), numeric: true, render: (x) => format.number(n(x.expired)) },
          { key: 'pending', header: r('pending'), numeric: true, render: (x) => format.number(n(x.pending)) },
          { key: 'covers', header: r('covers'), numeric: true, render: (x) => format.number(n(x.covers)) },
          {
            key: 'seatedCovers',
            header: r('seatedCoversHeader'),
            numeric: true,
            render: (x) => format.number(n(x.seatedCovers)),
          },
        ]}
        rows={bookingRows.map((x) => ({ ...x, id: x.storeId }))}
        ariaLabel={r('bookingsTitle')}
        loading={bookings.isLoading}
        empty={{ heading: r('noBookings') }}
      />
    </ReportFrame>
  );
}
